import React, { useEffect, useMemo, useState } from 'react';

import { useErrorBoundary } from 'react-error-boundary';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { HelperText } from '@pulse-web-ui/helper-text';
import { observer } from 'mobx-react-lite';

import { sendAnalyticEvent } from '@app/web-analytic';

import {
  type AgentBankDetailsFormParams,
  type UpdateProfileRequestParams,
  useUpdateProfile,
} from '@entities/index';

import {
  AnalyticEvent,
  BoundaryErrorType,
  FlkCode,
  addTestAttribute,
  getMismatches,
  removeSpaces,
  useStores,
} from '@shared/index';
import { FullWidthPrimaryButton } from '@shared/ui';

export interface SubmitBankDetailsProps {
  onNextStep?: (isContractReady?: boolean) => void;
  isLoading?: boolean;
}

export const SubmitBankDetails = observer(
  ({ onNextStep, isLoading }: SubmitBankDetailsProps) => {
    const { t } = useTranslation();
    const { showBoundary } = useErrorBoundary();
    const {
      MainStore: {
        agentContractStore: { setBankDetails, profile },
      },
    } = useStores();
    const {
      handleSubmit,
      formState: { errors },
      setError,
    } = useFormContext<AgentBankDetailsFormParams>();
    const [requestParams, setRequestParams] =
      useState<UpdateProfileRequestParams>({
        isComplete: false,
        profile: {
          ...profile,
          bankDetails: undefined,
        },
      });

    const { res, refetch, error, isFetching } = useUpdateProfile(
      {
        ...requestParams,
      },
      [requestParams]
    );

    useEffect(() => {
      if (
        !!requestParams?.profile?.bankDetails?.bic?.length &&
        !!requestParams?.profile?.bankDetails?.nameBank?.length &&
        !!requestParams?.profile?.bankDetails?.corAccount?.length &&
        !!requestParams?.profile?.bankDetails?.bankAccount?.length
      ) {
        refetch();
      }
    }, [requestParams]);

    useEffect(() => {
      if (!isFetching && res && !!requestParams?.profile?.bankDetails) {
        setBankDetails(requestParams?.profile?.bankDetails);
        sendAnalyticEvent(AnalyticEvent.AGENT_AGREEMENT_TOSTEP_FORMS);
        onNextStep?.(res.isContractReady);
      }
    }, [isFetching, res]);

    useEffect(() => {
      if (error) {
        const mismatches = getMismatches(error);

        if (!!mismatches) {
          mismatches.forEach(({ code }) => {
            if (code === FlkCode.AGT_01_0011) {
              setError('root.serverError', {
                type: code,
              });
            } else {
              showBoundary(BoundaryErrorType.SomethingWentWrong);
            }
          });
        } else {
          showBoundary(BoundaryErrorType.SomethingWentWrong);
        }
      }
    }, [error]);

    const submitPage = handleSubmit((values) => {
      const { bic, nameBank, bankAccount, corAccount } = values;

      setRequestParams((prevParams) => ({
        ...prevParams,
        profile: {
          ...prevParams.profile,
          bankDetails: {
            bic,
            nameBank,
            bankAccount: removeSpaces(bankAccount),
            corAccount: removeSpaces(corAccount),
          },
        },
      }));
    });

    const isDisabled = useMemo(
      () => !!Object.keys(errors).length || isLoading,
      [errors, isLoading]
    );

    return (
      <HelperText
        status='error'
        message={
          errors?.root?.serverError.type &&
          t('COMMON:errors.duplicatedData', {
            code: errors.root.serverError.type,
          })
        }
      >
        <FullWidthPrimaryButton
          size='l'
          onClick={submitPage}
          disabled={isDisabled}
          isLoading={isFetching}
          {...addTestAttribute('agentBankDetails.submitButton')}
        >
          {t('COMMON:buttons.continue')}
        </FullWidthPrimaryButton>
      </HelperText>
    );
  }
);
